import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://wpp.debugin.com/ai";

interface AuthUser {
  phone: string;
  plan: string;
  session: string;
  token: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  tempPhone: string | null;
  login: (phone: string) => Promise<{ success: boolean; error?: string }>;
  verify: (phone: string, code: string) => Promise<{ success: boolean; error?: string }>;
  logout: (redirectPath?: string) => void;
  updateUser: (fields: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- The provider and its public hook intentionally share one context module.
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tempPhone, setTempPhone] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("auth_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as AuthUser;
        setUser(parsed);
      } catch {
        localStorage.removeItem("auth_user");
      }
    }
    setIsLoading(false);
  }, []);

  const updateUser = useCallback((fields: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...fields };
      localStorage.setItem("auth_user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const login = useCallback(async (phone: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/sign-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (data.success) {
        setTempPhone(phone);
        return { success: true };
      }
      return { success: false, error: data.error || "Failed to send OTP" };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  }, []);

  const verify = useCallback(async (phone: string, code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code }),
      });
      const data = await response.json();
      if (data.success && data.data) {
        const userData: AuthUser = {
          phone: data.data.phone,
          plan: data.data.plan,
          session: data.data.session,
          token: data.data.token,
        };
        setUser(userData);
        setTempPhone(null);
        localStorage.setItem("auth_user", JSON.stringify(userData));
        return { success: true };
      }
      return { success: false, error: data.error || "Invalid OTP" };
    } catch {
      return { success: false, error: "Network error. Please try again." };
    }
  }, []);

  const logout = useCallback((redirectPath = "/login") => {
    setUser(null);
    setTempPhone(null);
    localStorage.removeItem("auth_user");
    navigate(redirectPath);
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        tempPhone,
        login,
        verify,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
